import { useMemo } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { KeyRound, LogOut, Menu as IconoMenu, Wheat } from 'lucide-react';
import { MenuArbol } from '@/shared/components/ui/MenuArbol';
import { formatearFecha, hoy } from '@/shared/lib/fecha';
import { useEsMovil } from '@/shared/hooks/useEsMovil';
import { useCerrarSesion, useSesion } from '@/shared/auth/useSesion';
import { BarraDeSeccion } from './BarraDeSeccion';
import {
  areaDeRuta,
  menuPara,
  rutaActivaDelMenu,
  seccionDeRuta,
  seccionesVisibles,
  tituloPorRuta,
} from './menu';
import { useUiStore } from './stores/ui.store';

/**
 * Estructura principal: encabezado arriba, menu en arbol a la izquierda y el contenido
 * a la derecha. El menu depende de los permisos del rol: solo aparecen las areas que
 * el usuario puede leer.
 *
 * En tablet el menu se pliega: el operador de balanza necesita todo el ancho para el
 * formulario de romaneo.
 */
export function Layout() {
  const { data: sesion, isPending } = useSesion();
  const { mutate: salir } = useCerrarSesion();
  const navegar = useNavigate();
  const ubicacion = useLocation();
  const esMovil = useEsMovil();
  const { menuAbierto, alternarMenu, cerrarMenu } = useUiStore();

  const menu = useMemo(() => (sesion ? menuPara(sesion.permisos, esMovil) : []), [sesion, esMovil]);

  if (isPending) return <p className="p-6 text-slate-500">Cargando…</p>;
  if (!sesion) return <Navigate to="/login" replace />;

  // Las entradas del menu pueden llevar query (`?pestania=…`): se compara con la URL completa.
  const rutaCompleta = ubicacion.pathname + ubicacion.search;
  const areaActual = areaDeRuta(rutaCompleta);
  const puedeLeer = !areaActual || sesion.permisos[areaActual].leer;
  const titulo = tituloPorRuta(rutaCompleta);
  const seccionActual = seccionesVisibles(sesion.permisos, esMovil).find(
    (seccion) => seccion.id === seccionDeRuta(rutaCompleta)?.id,
  );

  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <header className="z-30 flex items-center gap-2 bg-linear-to-r from-marca-800 to-marca-500 px-3 py-2 text-white shadow">
        <button
          type="button"
          className="rounded p-1.5 hover:bg-white/15 lg:hidden"
          onClick={alternarMenu}
          aria-label={menuAbierto ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={menuAbierto}
        >
          <IconoMenu className="size-5" aria-hidden="true" />
        </button>

        <div className="flex items-center gap-2">
          <span className="grid size-8 place-items-center rounded bg-white/15">
            <Wheat className="size-5" aria-hidden="true" />
          </span>
          <div className="leading-tight">
            <p className="text-lg font-bold tracking-wide">JAC</p>
            <p className="hidden text-xs whitespace-nowrap text-marca-100 sm:block">
              Planta Villa del Rosario
            </p>
          </div>
        </div>

        <p className="ml-4 hidden flex-1 truncate text-sm text-marca-100 md:block">{titulo}</p>

        <div className="ml-auto flex min-w-0 items-center gap-1 text-sm sm:gap-3">
          <span className="hidden text-marca-100 sm:inline">{formatearFecha(hoy())}</span>
          <span className="max-w-32 truncate rounded bg-white/15 px-2 py-1 sm:max-w-none">
            <span className="hidden sm:inline">Usuario: </span>
            <strong>{sesion.usuario}</strong>
            {sesion.rol ? (
              <span className="ml-1 hidden text-marca-100 xl:inline">({sesion.rol})</span>
            ) : null}
          </span>
          <Link
            to="/cambiar-clave"
            className="flex items-center gap-1 rounded px-2 py-1.5 hover:bg-white/15"
            title="Cambiar contraseña"
            aria-label="Cambiar contraseña"
          >
            <KeyRound className="size-4" aria-hidden="true" />
            <span className="hidden sm:inline">Clave</span>
          </Link>
          <button
            type="button"
            onClick={() => salir()}
            aria-label="Salir"
            className="flex items-center gap-1 rounded px-2 py-1.5 hover:bg-white/15"
          >
            <LogOut className="size-4" aria-hidden="true" />
            <span className="hidden sm:inline">Salir</span>
          </button>
        </div>
      </header>

      <div className="flex min-h-0 flex-1">
        {menuAbierto ? (
          <button
            type="button"
            aria-label="Cerrar menú"
            onClick={cerrarMenu}
            className="fixed inset-0 top-12 z-10 bg-slate-900/40 lg:hidden"
          />
        ) : null}
        <aside
          className={[
            'w-64 shrink-0 overflow-y-auto bg-marca-800 lg:block',
            menuAbierto ? 'fixed inset-y-0 top-12 left-0 z-20 block shadow-xl' : 'hidden',
          ].join(' ')}
        >
          <MenuArbol
            nodos={menu}
            rutaActiva={rutaActivaDelMenu(rutaCompleta, sesion.permisos)}
            abiertosPorDefecto={menu.map((nodo) => nodo.id)}
            onNavegar={(ruta) => {
              cerrarMenu();
              void navegar(ruta);
            }}
          />
        </aside>

        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto p-3">
          <BarraDeSeccion pestanias={seccionActual?.pestanias ?? []} />
          {puedeLeer ? (
            <Outlet />
          ) : (
            <p role="alert">No tenés permiso para acceder a esta sección.</p>
          )}
        </main>
      </div>
    </div>
  );
}
